import { createSelector } from '@reduxjs/toolkit';
import isEqual from 'lodash/isEqual';

import { Action } from '../..';

// Types
import { ReduxState } from '../types';
import { LoaderAction, LoadersState } from '../types/loaders';
import { START_LOADER_ACTION, STOP_LOADER_ACTION } from './action-types';
import Dictionary from '@/types/dictionary';

const INITIAL_STATE: LoadersState = {
  actions: [],
};

// Reducers
export default function reducer(state: LoadersState = INITIAL_STATE, { type, payload }: Action): LoadersState {
  const { actions } = state;
  switch (type) {
    case START_LOADER_ACTION:
      return {
        ...state,
        actions: [...actions, payload],
      };
    case STOP_LOADER_ACTION:
      if (payload.params) {
        return {
          ...state,
          actions: actions.filter(action => !isEqual(action, payload)),
        };
      }

      return {
        ...state,
        actions: actions.filter(action => action.name !== payload.name),
      };
    default: {
      return state;
    }
  }
}

// Action creators
export const startLoaderAction = (name: string, params?: Dictionary<unknown>) => ({
  type: START_LOADER_ACTION,
  payload: { name, params },
});

export const stopLoaderAction = (name: string, params?: Dictionary<unknown>) => ({
  type: STOP_LOADER_ACTION,
  payload: { name, params },
});

// Selectors
const mapActionParams = (actions: LoaderAction[], actionsToCheck: string[], shouldMapParams: boolean) => {
  if (shouldMapParams) {
    const params = actions
      .filter(action => actionsToCheck.includes(action.name))
      .reduce((acc: Dictionary<unknown>[], action: LoaderAction) => {
        if (action.params) {
          return [...acc, action.params];
        }

        return acc;
      }, []);

    return params;
  }

  return [];
};

// Base slice selectors
const selectLoaderActions = (state: ReduxState) => state.ui.loaders.actions;

// Returns true if at least one of the actions to check are in state
export const selectIsLoadingSome = createSelector(
  [selectLoaderActions, (_: ReduxState, actionsToCheck: string[]) => actionsToCheck],
  (actions, actionsToCheck) => actions.length > 0 && actions.some(action => actionsToCheck.includes(action.name)),
);

export const selectIsLoadingParamsSome = createSelector(
  [selectLoaderActions, (_: ReduxState, actionsToCheck: string[]) => actionsToCheck, selectIsLoadingSome],
  mapActionParams,
);

// Returns true if all of the actions to check are in state
export const selectIsLoadingEvery = createSelector(
  [selectLoaderActions, (_: ReduxState, actionsToCheck: string[]) => actionsToCheck],
  (actions, actionsToCheck) => {
    const actionsArray = actions.reduce((acc: string[], action) => [...acc, action.name], []);
    return actions.length > 0 && actionsToCheck.every(action => actionsArray.includes(action));
  },
);

export const selectIsLoadingParamsEvery = createSelector(
  [selectLoaderActions, (_: ReduxState, actionsToCheck: string[]) => actionsToCheck, selectIsLoadingEvery],
  mapActionParams,
);
